/**
 * Screenshots uploaded onto a review's slides — see migration 138.
 *
 * One image per review per slot. The client payload carries only which slots
 * have one, their titles and upload times (`AmSbr.SlideImages`); the image is
 * fetched on its own from `imagePath`.
 */
export const SLIDE_IMAGE_SLOTS = [
  'support',
  'backup',
  'backup2',
  'backup3',
  'service1',
  'service2',
  'service3',
  'service4',
  'feedback',
  'feedback2',
  /* The Agent Health slide (10a): a screenshot to show in place of the live
     Automate figures — for a client not mapped to Automate, or a view of the
     RMM the figures do not cover. */
  'agentHealth',
  /* The Technical Support slides a review adds itself, two screenshots each
     — see migration 202 and lib/supportSlides.ts. */
  'support1a',
  'support1b',
  'support2a',
  'support2b',
  'support3a',
  'support3b',
  'support4a',
  'support4b',
] as const;
export type SlideImageSlot = (typeof SLIDE_IMAGE_SLOTS)[number];

/** The Backup Review slide's slots, in the order they are laid out (140). */
export const BACKUP_SLOTS: SlideImageSlot[] = ['backup', 'backup2', 'backup3'];

/** The Service Information slide's slots, in the order they are laid out. */
/* Three since the deck asked for three. 'service4' stays a valid slot so a
   screenshot saved there before is not an error — it is just no longer shown. */
export const SERVICE_SLOTS: SlideImageSlot[] = ['service1', 'service2', 'service3'];

/** The screenshots of each added Technical Support slide, by its place in
 *  the review's list of them (202). Four slides, two shots each, so a slide
 *  added, filled in and removed does not take another review's slots. */
export const SUPPORT_SLIDE_SLOTS: SlideImageSlot[][] = [
  ['support1a', 'support1b'],
  ['support2a', 'support2b'],
  ['support3a', 'support3b'],
  ['support4a', 'support4b'],
];

/** The User Feedback slide: a large screenshot in the middle and a smaller one
 *  in the upper right. */
export const FEEDBACK_SLOTS: SlideImageSlot[] = ['feedback', 'feedback2'];

export interface SavedSlideImage {
  title: string;
  uploadedUtc: string;
}

/** Never throws: a malformed value reads as no images. */
export function parseSlideImages(raw: string | null | undefined): Partial<Record<SlideImageSlot, SavedSlideImage>> {
  const out: Partial<Record<SlideImageSlot, SavedSlideImage>> = {};
  if (!raw) return out;
  try {
    const rows = JSON.parse(raw) as { Slot?: string; Title?: string | null; UploadedUtc?: string }[];
    for (const r of Array.isArray(rows) ? rows : []) {
      if (r.Slot && (SLIDE_IMAGE_SLOTS as readonly string[]).includes(r.Slot) && r.UploadedUtc) {
        out[r.Slot as SlideImageSlot] = { title: r.Title ?? '', uploadedUtc: r.UploadedUtc };
      }
    }
  } catch {
    /* none */
  }
  return out;
}

/** The image's address, versioned by upload time so a replaced one reloads. */
export const imagePath = (sbrId: string, slot: SlideImageSlot, uploadedUtc: string) =>
  `/am/sbrs/${sbrId}/images/${slot}?v=${encodeURIComponent(uploadedUtc)}`;
